import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { examAssignmentsAPI, examsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import AddExamModal from "./AddExamModal";

const AssignExamModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    candidateId: "",
    examId: "",
    dateOfExam: "",
    graceTime: "60", // Default 60 minutes
    sendInvitationEmail: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddExamModalOpen, setIsAddExamModalOpen] = useState(false);
  const [isExamLocked, setIsExamLocked] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        candidateId: "",
        examId: "",
        dateOfExam: "",
        graceTime: "60",
        sendInvitationEmail: true,
      });
      setIsExamLocked(false);
    }
  }, [isOpen]);

  const { data: candidatesData, refetch: refetchCandidates } = useQuery({
    queryKey: ["available-candidates", formData.examId],
    queryFn: () =>
      examAssignmentsAPI.getAvailableCandidates({
        examId: formData.examId,
      }),
    enabled: isOpen && !!formData.examId,
  });

  const { data: examsData, refetch: refetchExams } = useQuery({
    queryKey: ["exams-for-assignment"],
    queryFn: () => examsAPI.getAll({ limit: 100 }),
    enabled: isOpen,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.candidateId || !formData.examId || !formData.dateOfExam) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const examDateTime = new Date(formData.dateOfExam);

      await examAssignmentsAPI.create({
        ...formData,
        dateOfExam: examDateTime.toISOString(),
        graceTime: parseInt(formData.graceTime),
        userTimezone,
      });

      toast({
        title: "Exam assigned successfully",
        description: `Including ${formData.graceTime} minute grace period`,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to assign exam",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearDateTime = () => {
    setFormData(prev => ({ ...prev, dateOfExam: "" }));
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours()); // Minimum 1 hour from now
    return format(now, "yyyy-MM-dd'T'HH:mm");
  };

  const handleAddExamSuccess = async () => {
    // Refetch exams to get the newly created exam
    const result = await refetchExams();
    
    // Get the most recently created exam (first in the list)
    if (result.data?.data?.exams && result.data.data.exams.length > 0) {
      const newExam = result.data.data.exams[0];
      
      // Auto-select the newly created exam
      setFormData((prev) => ({ ...prev, examId: newExam._id }));
      
      // Lock the exam dropdown
      setIsExamLocked(true);
      
      toast({
        title: "Exam created and selected",
        description: `"${newExam.name}" has been automatically selected`,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Exam to Candidate</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="examId">Select Exam *</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.examId}
                  onValueChange={(value) => handleChange("examId", value)}
                  disabled={isExamLocked}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Choose an exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {examsData?.data?.exams?.map((exam) => (
                      <SelectItem key={exam._id} value={exam._id}>
                        {exam.name} ({exam.totalTime} min)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsAddExamModalOpen(true)}
                  title="Create new exam"
                  className="flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {isExamLocked && (
                <p className="text-sm text-blue-600">
                  Newly created exam is selected. Close and reopen to change.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="candidateId">Select Candidate *</Label>
              <Select
                value={formData.candidateId}
                onValueChange={(value) => handleChange("candidateId", value)}
                disabled={!formData.examId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !formData.examId
                        ? "Select an exam first"
                        : "Choose a candidate"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {candidatesData?.data?.myCandidates?.length > 0 && (
                    <>
                      <SelectItem disabled value="my-candidates-header">
                        My Candidates
                      </SelectItem>
                      {candidatesData.data.myCandidates.map((candidate) => (
                        <SelectItem key={candidate._id} value={candidate._id}>
                          {candidate.name} ({candidate.email})
                        </SelectItem>
                      ))}
                    </>
                  )}

                  {candidatesData?.data?.otherCandidates?.length > 0 && (
                    <>
                      {candidatesData.data.myCandidates?.length > 0 && (
                        <SelectItem disabled value="separator">
                          ---
                        </SelectItem>
                      )}
                      <SelectItem disabled value="other-candidates-header">
                        Company Candidates
                      </SelectItem>
                      {candidatesData.data.otherCandidates.map((candidate) => (
                        <SelectItem key={candidate._id} value={candidate._id}>
                          {candidate.name} ({candidate.email})
                        </SelectItem>
                      ))}
                    </>
                  )}

                  {!candidatesData?.data?.myCandidates?.length &&
                    !candidatesData?.data?.otherCandidates?.length && (
                      <SelectItem disabled value="no-candidates">
                        {!formData.examId
                          ? "Select an exam first"
                          : "No available candidates for this exam"}
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="dateOfExam">Exam Date & Time *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearDateTime}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </Button>
            </div>
            <Input
              id="dateOfExam"
              type="datetime-local"
              value={formData.dateOfExam}
              min={getMinDateTime()}
              onChange={(e) => handleChange("dateOfExam", e.target.value)}
              required
            />
            <p className="text-sm text-gray-500">
              Select when the candidate should take the exam (your local
              timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone})
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="graceTime">Grace Time (minutes)</Label>
            <Input
              id="graceTime"
              type="number"
              min="5"
              max="1440"
              step="5"
              value={formData.graceTime}
              onChange={(e) => handleChange("graceTime", e.target.value)}
            />
            <p className="text-sm text-gray-500">
              Additional time after exam when it will still be available
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="sendInvitationEmail"
              checked={formData.sendInvitationEmail}
              onCheckedChange={(checked) =>
                handleChange("sendInvitationEmail", checked)
              }
            />
            <Label htmlFor="sendInvitationEmail">
              Send invitation email to candidate
            </Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Assigning..." : "Assign Exam"}
            </Button>
          </div>
        </form>
      </DialogContent>

      <AddExamModal
        isOpen={isAddExamModalOpen}
        onClose={() => setIsAddExamModalOpen(false)}
        onSuccess={handleAddExamSuccess}
      />
    </Dialog>
  );
};

export default AssignExamModal;